// =============================================================================
// SITE-WIDE RUNTIME CONFIGURATION
// =============================================================================
//
// Central place for values that must be tweakable without hunting through the
// component tree. Keep this file *tiny* and *safe to import from anywhere*
// (frontend-only, no runtime side effects).
//
// -----------------------------------------------------------------------------
// SECTIONS
// -----------------------------------------------------------------------------
//   1. Statuto PDF          → set STATUTE_PDF_URL when the official PDF is ready
//   2. Brand palette         → readonly reference (source of truth in Tailwind)
//   3. Home rotation tuning  → knobs for the "Le attività del centro" section
//   4. Future: MEMBER_AREA   → reserved namespace for the upcoming Area Riservata
//
// =============================================================================

// -----------------------------------------------------------------------------
// DOMAIN (single source of truth)
// -----------------------------------------------------------------------------
// The official production domain. Change ONLY here (or override via env).
// Read from REACT_APP_FRONTEND_URL if set; otherwise falls back to production
// default. Any component needing the current site URL/email should import
// SITE_DOMAIN.* instead of hardcoding literals.
export const SITE_DOMAIN = {
  URL: (process.env.REACT_APP_FRONTEND_URL || "https://sanluriccn.it").replace(/\/+$/, ""),
  HOST: (process.env.REACT_APP_FRONTEND_URL || "https://sanluriccn.it")
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, ""),
  CONTACT_EMAIL: process.env.REACT_APP_CONTACT_EMAIL || "info@sanluriccn.it",
};

export const SITE_CONFIG = {
  // ---------------------------------------------------------------------------
  // 1. STATUTO
  // ---------------------------------------------------------------------------
  // When the Statuto PDF is finalized:
  //   1. Upload it to Supabase Storage → bucket `business-media` → path
  //      `settings/statuto.pdf`
  //   2. Copy the "public URL" from the Storage UI
  //   3. Replace `null` below with the URL string
  // The Chi siamo page reads this constant and swaps the discreet placeholder
  // for a "Scarica lo Statuto (PDF)" button automatically. No other code
  // change required.
  STATUTE_PDF_URL: null,

  // ---------------------------------------------------------------------------
  // 2. BRAND PALETTE (readonly reference)
  // ---------------------------------------------------------------------------
  // Do NOT read these at render time — Tailwind classes (`bg-[#12964A]`, etc.)
  // are the source of truth. This block exists only for humans reviewing the
  // approved brand identity.
  BRAND: {
    green_primary: "#12964A", // Colore principale — azioni primarie, link
    green_light: "#6FC247",   // Verde chiaro — stati positivi, accenti
    yellow_accent: "#F4E500", // Giallo — accento istituzionale (CTA "Associati")
    text: "#1F2937",          // Testo principale
    bg: "#F8F9FA",            // Sfondo neutro sezioni
  },

  // ---------------------------------------------------------------------------
  // 3. HOME ROTATION
  // ---------------------------------------------------------------------------
  // The Home spotlight fetches N random published businesses each load and
  // remembers the last shown IDs in sessionStorage to avoid immediate repeats.
  //
  // Why these numbers:
  //   - SPOTLIGHT_LIMIT: number of cards displayed on Home
  //   - SESSION_MEMORY:  how many past IDs we exclude on next load. Larger →
  //     less repetition but eventually falls back to full pool when memory
  //     >= published_count.
  HOME: {
    SPOTLIGHT_LIMIT: 6,
    SESSION_MEMORY: 18, // 3 * SPOTLIGHT_LIMIT
  },

  // ---------------------------------------------------------------------------
  // 4. FUTURE — MEMBER_AREA (Area Riservata)
  // ---------------------------------------------------------------------------
  // Placeholder namespace reserved for the upcoming members-only area
  // (accessible via password / member login). Do NOT populate yet. Files that
  // will slot in here in a future iteration:
  //
  //   Frontend:
  //     /app/frontend/src/pages/members/                  ← new folder
  //       ├── MembersLogin.jsx
  //       ├── MembersDashboard.jsx
  //       ├── BrandKit.jsx          (Logo PNG, SVG, manuale marchio)
  //       └── DocumentiRiservati.jsx
  //     /app/frontend/src/lib/api.js
  //       └── memberApi = { login, listResources, downloadResource }
  //     /app/frontend/src/App.js
  //       └── new <Route path="/area-riservata/*" element={<MembersLayout />} />
  //
  //   Backend:
  //     /app/backend/routes/members.py                    ← new file
  //       └── endpoints under /api/members/*  (login, resources CRUD)
  //     /app/backend/models/member.py                     ← new file
  //     Supabase: new table `member_resources` + RLS policy authenticated-read
  //
  // Extension points already in place:
  //   - AuthContext (currently admin-only) can be widened to `role: admin|member`
  //   - Header CTA can gain a "Area Riservata" link when MEMBER_AREA.ENABLED=true
  //   - This SITE_CONFIG object accepts new keys without breaking imports
  //
  // Keep this section as documentation only until the feature is greenlit.
  MEMBER_AREA: {
    ENABLED: false,
  },

  // ---------------------------------------------------------------------------
  // 5. EDITORIAL CMS WIRE (Patch V11, Phase 3)
  // ---------------------------------------------------------------------------
  // When true, public components resolve their copy in this priority order:
  //   1. `content?.blocks[block_key]?.content[field]` (published DB value)
  //   2. Empty string ''  →  intentionally hidden (respected as-is)
  //   3. Hardcoded fallback string in the component
  //
  // Flipping this to `false` restores the pre-CMS behaviour instantly (no code
  // change needed). Useful as an emergency rollback if the DB becomes unhealthy.
  ENABLE_CMS_WIRE: true,
};
